import { JsonPipe } from "@angular/common";
import { Component } from "@angular/core";
import { ApiSpecificationModel } from "@app/manual/models/api-models/api-specification-model";
import { ApiSpecComponent } from "../../api-spec";

@Component({
    selector: "app-incidents-create-incident-api-manual",
    templateUrl: "../../api-spec.html",
    styleUrls: ["../../api-spec.scss"],
    standalone: true,
    imports: [JsonPipe],
})
export class IncidentsCreateApiManualComponent extends ApiSpecComponent {
    initSpecs() {
        return <ApiSpecificationModel>{
            name: "Create New Incident",
            description: "This endpoint can be used to create a new incident.",
            httpMethod: "POST",
            path: "/incidents",

            parameters: [
                {
                    name: "ClassificationId",
                    description: "The ID of the Classification of the incident to create",
                    in: "body",
                    paramType: "uuid",
                    required: false,
                    example: "00000000-0000-0000-0000-000000000000",
                },
                {
                    name: "OrganizationId",
                    description: "The ID of the organization associated with the incident",
                    in: "body",
                    paramType: "uuid",
                    required: true,
                    example: "00000000-0000-0000-0000-000000000000",
                },
                {
                    name: "ReportedById",
                    description:
                        "The ID of the user who reported the incident, if none is provided, the current user will be used",
                    in: "body",
                    paramType: "uuid",
                    required: false,
                    example: "00000000-0000-0000-0000-000000000000",
                },
                {
                    name: "Name",
                    description: "Name of the incident to create",
                    in: "body",
                    paramType: "string",
                    required: true,
                    example: "Incident Name",
                },
                {
                    name: "Description",
                    description: "Description of the incident",
                    in: "body",
                    paramType: "string",
                    required: false,
                    example: "Incident Description",
                },
                {
                    name: "OccurredOn",
                    description: "The date and time when the incident occurred",
                    in: "body",
                    paramType: "string",
                    required: false,
                    example: "2023-01-01T12:00:00Z",
                },
                {
                    name: "DetectedOn",
                    description:
                        "The date and time when the incident was detected, if none is provided, the current date and time will be used",
                    in: "body",
                    paramType: "string",
                    required: false,
                    example: "2023-01-01T12:00:00Z",
                },
                {
                    name: "DueDate",
                    description: "The date and time when the incident is due",
                    in: "body",
                    paramType: "string",
                    required: false,
                    example: "2023-01-01T12:00:00Z",
                },
                {
                    name: "RootCause",
                    description: "The root cause of the incident",
                    in: "body",
                    paramType: "string",
                    required: false,
                    example: "Root cause of the incident",
                },
                {
                    name: "InternalIdentifier",
                    description: "The internal identifier for the incident",
                    in: "body",
                    paramType: "string",
                    required: false,
                    example: "Internal Identifier",
                },
                {
                    name: "Impact",
                    description: "The impact of the incident",
                    in: "body",
                    paramType: "string",
                    required: false,
                    example: "High",
                },
                {
                    name: "IncidentTypeIds",
                    description: "Id's of the incident types to be linked to the incident",
                    in: "body",
                    paramType: "array of uuids",
                    required: false,
                    example: [
                        "00000000-0000-0000-0000-000000000000",
                        "00000000-0000-0000-0000-000000000001",
                    ],
                },
                {
                    name: "BusinessDimensionIds",
                    description: "Id's of the business dimensions to be linked to the incident",
                    in: "body",
                    paramType: "array of uuids",
                    required: false,
                    example: [
                        "00000000-0000-0000-0000-000000000000",
                        "00000000-0000-0000-0000-000000000001",
                    ],
                },
                {
                    name: "FrameworkDimensionIds",
                    description: "Id's of the framework dimensions to be linked to the incident",
                    in: "body",
                    paramType: "array of uuids",
                    required: false,
                    example: [
                        "00000000-0000-0000-0000-000000000000",
                        "00000000-0000-0000-0000-000000000001",
                    ],
                },
                {
                    name: "RootCauseCategoryIds",
                    description: "Id's of the root cause categories to be linked to the incident",
                    in: "body",
                    paramType: "array of uuids",
                    required: false,
                    example: [
                        "00000000-0000-0000-0000-000000000000",
                        "00000000-0000-0000-0000-000000000001",
                    ],
                },
                {
                    name: "CausedByOrganizationIds",
                    description: "Id's of the organizations that caused the incident",
                    in: "body",
                    paramType: "array of uuids",
                    required: false,
                    example: [
                        "00000000-0000-0000-0000-000000000000",
                        "00000000-0000-0000-0000-000000000001",
                    ],
                },
                {
                    name: "AffectedOrganizationIds",
                    description: "Id's of the organizations affected by the incident",
                    in: "body",
                    paramType: "array of uuids",
                    required: false,
                    example: [
                        "00000000-0000-0000-0000-000000000000",
                        "00000000-0000-0000-0000-000000000001",
                    ],
                },
                {
                    name: "CausedByThirdPartyIds",
                    description: "Id's of the third parties that caused the incident",
                    in: "body",
                    paramType: "array of uuids",
                    required: false,
                    example: [
                        "00000000-0000-0000-0000-000000000000",
                        "00000000-0000-0000-0000-000000000001",
                    ],
                },
                {
                    name: "AffectedThirdPartyIds",
                    description: "Id's of the third parties affected by the incident",
                    in: "body",
                    paramType: "array of uuids",
                    required: false,
                    example: [
                        "00000000-0000-0000-0000-000000000000",
                        "00000000-0000-0000-0000-000000000001",
                    ],
                },
                {
                    name: "ResponsibleIds",
                    description: "Id's of the responsible users linked to the incident",
                    in: "body",
                    paramType: "array of uuids",
                    required: false,
                    example: [
                        "00000000-0000-0000-0000-000000000000",
                        "00000000-0000-0000-0000-000000000001",
                    ],
                },
                {
                    name: "AssessorIds",
                    description: "Id's of the users assessing the incident",
                    in: "body",
                    paramType: "array of uuids",
                    required: false,
                    example: [
                        "00000000-0000-0000-0000-000000000000",
                        "00000000-0000-0000-0000-000000000001",
                    ],
                },
                {
                    name: "InformedIds",
                    description: "Id's of the users informed about the incident",
                    in: "body",
                    paramType: "array of uuids",
                    required: false,
                    example: [
                        "00000000-0000-0000-0000-000000000000",
                        "00000000-0000-0000-0000-000000000001",
                    ],
                },
                {
                    name: "IncidentCustomFields",
                    description:
                        "The custom fields to set for the incident. CustomFieldId and Value both are required. Value is always a string, but can represent a comma separated list of optionIds, a single optionId, an actual string, a date value, or 'true': depending on the type of custom field being filled",
                    in: "body",
                    paramType: "array of models",
                    required: false,
                    example: [
                        {
                            CustomFieldId: "00000000-0000-0000-0000-000000000001",
                            Value: "1",
                        },
                        {
                            CustomFieldId: "00000000-0000-0000-0000-000000000002",
                            Value: "api custom field text",
                        },
                        {
                            CustomFieldId: "00000000-0000-0000-0000-000000000004",
                            Value: "2025-03-15",
                        },
                        {
                            CustomFieldId: "00000000-0000-0000-0000-000000000004",
                            Value: "2026-03-30T09:30:00.0000000Z",
                        },
                        {
                            CustomFieldId: "00000000-0000-0000-0000-000000000005",
                            Value: "true",
                        },
                        {
                            CustomFieldId: "00000000-0000-0000-0000-000000000006",
                            Value: "00000000-0000-0000-0000-000000000009,00000000-0000-0000-0000-000000000007",
                        },
                    ],
                },
            ],

            responses: [
                {
                    code: "200",
                    description: "OK",
                    example: "00000000-0000-0000-0000-000000000000",
                },
            ],
        };
    }
}
